import React, { useEffect } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { V2ThemeProvider } from './theme/V2ThemeProvider';
import { V2FiltersProvider } from './context/V2FiltersContext';
import { V2Layout, NavEntry } from './layouts/V2Layout';
import { V2Dashboard } from './pages/Dashboard';
import { V2GlobalView } from './pages/GlobalView';
import { V2PointsOfSale } from './pages/PointsOfSale';
import { V2RawData } from './pages/RawData';
import { V2MarketMap } from './pages/MarketMap';
import { V2PressReview } from './pages/PressReview';
import { V2Notes } from './pages/Notes';
import { CrmSalesTracking } from './pages/CrmSalesTracking';
import { ListMarkets } from './pages/ListMarkets';
import { V2Alerts } from './pages/Alerts';
import { CompetitorsPositioning } from './pages/CompetitorsPositioning';
import { RoiDashboard } from './pages/RoiDashboard';
import { Primeurs } from './pages/Primeurs';
import { OnlineSalesOverview } from './pages/OnlineSalesOverview';
import { OnlineSalesDetailed } from './pages/OnlineSalesDetailed';
import { AuctionSales } from './pages/AuctionSales';
import { Documents } from './pages/Documents';
import { NegoceStats } from './pages/NegoceStats';
import { MediaReporting } from './pages/MediaReporting';
import { IntermediarySales } from './pages/IntermediarySales';

/**
 * Navigation config for the v2 sidebar.
 * Matches the structure from the V6 prototype.
 */
const navigation: NavEntry[] = [
  {
    id: 'home',
    label: 'Home',
    icon: 'home',
    path: '/v2/dashboard'
  },
  {
    id: 'distribution',
    label: 'Distribution',
    icon: 'distribution',
    path: '/v2/distribution',
    items: [
      {
        id: 'global-view',
        label: 'Global View',
        path: '/v2/distribution/global-view'
      },
      { id: 'pos', label: 'Points of Sale', path: '/v2/distribution/pos' },
      {
        id: 'competitors',
        label: 'Competitors positioning',
        path: '/v2/distribution/competitors'
      },
      { id: 'map', label: 'Market Map', path: '/v2/distribution/map' },
      { id: 'crm', label: 'CRM Sales Tracking', path: '/v2/distribution/crm' },
      {
        id: 'alerts',
        label: 'Alerts',
        path: '/v2/distribution/alerts',
        count: 7
      },
      { id: 'raw', label: 'Raw Data', path: '/v2/distribution/raw-data' }
    ]
  },
  {
    id: 'pilotage',
    label: 'Pilotage',
    icon: 'pilotage',
    path: '/v2/pilotage',
    items: [
      { id: 'roi', label: 'ROI Dashboard', path: '/v2/pilotage/roi' },
      { id: 'markets', label: 'List Markets', path: '/v2/pilotage/markets' },
      { id: 'price', label: 'Price Analysis', path: '/v2/pilotage/price' }
    ]
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: 'communication',
    path: '/v2/communication',
    items: [
      { id: 'press', label: 'Press Review', path: '/v2/communication/press' },
      { id: 'notes', label: 'Notes', path: '/v2/communication/notes' },
      { id: 'media', label: 'Media Reporting', path: '/v2/communication/media' }
    ]
  },
  {
    id: 'campaigns',
    label: 'Campaigns',
    icon: 'campaigns',
    path: '/v2/campaigns',
    items: [
      {
        id: 'online-sales',
        label: 'Online Sales',
        path: '/v2/campaigns/online-sales'
      },
      { id: 'primeurs', label: 'Primeurs', path: '/v2/campaigns/primeurs' },
      { id: 'auction', label: 'Auction Sales', path: '/v2/campaigns/auction' },
      { id: 'negoce', label: 'Stats de Négoce', path: '/v2/campaigns/negoce' },
      {
        id: 'intermediary',
        label: 'Intermediary Sales',
        path: '/v2/campaigns/intermediary'
      },
      { id: 'docs', label: 'Documents', path: '/v2/campaigns/docs' }
    ]
  }
];

interface V2AppProps {
  user: any;
  match: { path: string; url: string };
  history: any;
  location: { pathname: string };
}

const V2App: React.FC<V2AppProps> = ({ user, match, history, location }) => {
  const userInitials =
    user?.first_name && user?.last_name
      ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
      : '?';

  // Ybug visual feedback widget (free tier, native Asana integration).
  // Loaded only while the v2 app is mounted, so it never appears elsewhere.
  // The widget ID is public (embedded in the page for every visitor by
  // design), so it's safe to hardcode rather than treat as a secret.
  useEffect(() => {
    const YBUG_WIDGET_ID = '95zgbv930dsdfzmrccap';

    (window as any).ybug_settings = { id: YBUG_WIDGET_ID };
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.id = 'ybug-loader';
    script.src = `https://widget.ybug.io/button/${YBUG_WIDGET_ID}.js`;
    document.body.appendChild(script);

    return () => {
      document.getElementById('ybug')?.remove();
      document.getElementById('ybug-loader')?.remove();
      delete (window as any).ybug_settings;
      delete (window as any).Ybug;
    };
  }, []);

  return (
    <V2ThemeProvider>
      <V2FiltersProvider>
        <V2Layout
          navigation={navigation}
          activePath={location.pathname}
          userName={user?.first_name}
          userInitials={userInitials}
          onNavigate={(path) => history.push(path)}
        >
          <Switch>
            <Route
              exact
              path={`${match.path}/dashboard`}
              render={() => <V2Dashboard userName={user?.first_name} />}
            />
            {/* Distribution */}
            <Route
              path={`${match.path}/distribution/global-view`}
              component={V2GlobalView}
            />
            <Route
              path={`${match.path}/distribution/pos`}
              component={V2PointsOfSale}
            />
            <Route
              path={`${match.path}/distribution/competitors`}
              component={CompetitorsPositioning}
            />
            <Route
              path={`${match.path}/distribution/map`}
              component={V2MarketMap}
            />
            <Route
              path={`${match.path}/distribution/crm`}
              component={CrmSalesTracking}
            />
            <Route
              path={`${match.path}/distribution/alerts`}
              component={V2Alerts}
            />
            <Route
              path={`${match.path}/distribution/raw-data`}
              component={V2RawData}
            />
            <Route
              exact
              path={`${match.path}/distribution`}
              render={() => <PlaceholderPage title="Distribution Overview" />}
            />
            {/* Pilotage */}
            <Route
              path={`${match.path}/pilotage/roi`}
              component={RoiDashboard}
            />
            <Route
              path={`${match.path}/pilotage/markets`}
              component={ListMarkets}
            />
            <Route
              path={`${match.path}/pilotage/price`}
              render={() => <PlaceholderPage title="Price Analysis" />}
            />

            <Route
              exact
              path={`${match.path}/pilotage`}
              render={() => <PlaceholderPage title="Pilotage Overview" />}
            />
            {/* Communication */}
            <Route
              path={`${match.path}/communication/press`}
              component={V2PressReview}
            />
            <Route
              path={`${match.path}/communication/notes`}
              component={V2Notes}
            />
            <Route
              path={`${match.path}/communication/media`}
              component={MediaReporting}
            />
            <Route
              exact
              path={`${match.path}/communication`}
              render={() => (
                <Redirect to={`${match.path}/communication/press`} />
              )}
            />
            {/* Campaigns */}
            <Route
              exact
              path={`${match.path}/campaigns/online-sales`}
              component={OnlineSalesOverview}
            />
            <Route
              path={`${match.path}/campaigns/online-sales/detailed`}
              component={OnlineSalesDetailed}
            />
            <Route
              path={`${match.path}/campaigns/primeurs`}
              component={Primeurs}
            />
            <Route
              path={`${match.path}/campaigns/auction`}
              component={AuctionSales}
            />
            <Route
              path={`${match.path}/campaigns/negoce`}
              component={NegoceStats}
            />
            <Route
              path={`${match.path}/campaigns/intermediary`}
              component={IntermediarySales}
            />
            <Route
              path={`${match.path}/campaigns/docs`}
              component={Documents}
            />
            <Route
              exact
              path={`${match.path}/campaigns`}
              render={() => <PlaceholderPage title="Campaigns Overview" />}
            />
            {/* Default */}
            <Redirect from={match.path} to={`${match.path}/dashboard`} />
          </Switch>
        </V2Layout>
      </V2FiltersProvider>
    </V2ThemeProvider>
  );
};

/** Temporary placeholder for pages not yet implemented */
const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ padding: '24px 0' }}>
    <h2
      style={{
        fontFamily: 'var(--font-serif)',
        fontSize: '28px',
        fontWeight: 500,
        marginBottom: 8
      }}
    >
      {title}
    </h2>
    <p style={{ color: 'var(--ink-muted)', fontSize: '14px' }}>
      This page is under construction.
    </p>
  </div>
);

export default V2App;
